import { Fragment, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactElement } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  AlignLeft,
  ChevronLeft,
  ChevronRight,
  GitFork,
  GripVertical,
  Image,
  MessageCircle,
  MoreHorizontal,
  Music2,
  Plus,
  Sparkles,
  Trash2,
  TriangleAlert,
  UserRound,
} from "lucide-react";
import { IconButton, Popover } from "../../ui/index.js";
import { useTr, type Entry } from "../../i18n/context.js";
import type { InsertKind } from "../editor/block-templates.js";
import type { PresentationStep, StoryboardBranch, StoryboardSection } from "./presentation-model.js";

const S = {
  timeline: { zh: "帧时间轴", en: "Frame timeline" },
  position: { zh: "第 {current} / {total} 帧", en: "Frame {current} of {total}" },
  previous: { zh: "上一帧", en: "Previous frame" },
  next: { zh: "下一帧", en: "Next frame" },
  insertHere: { zh: "在这里插入一帧", en: "Insert a frame here" },
  drag: { zh: "拖动这一帧调整顺序", en: "Drag this frame to reorder" },
  remove: { zh: "删除这一帧", en: "Delete this frame" },
  addTitle: { zh: "下一帧，玩家会看到什么？", en: "What will the player see next?" },
  addHint: { zh: "常用模板放在前面；更多功能收在“其他”里。", en: "Common templates first; specialist tools live under More." },
  scene: { zh: "场景", en: "Scene" },
  sceneHint: { zh: "背景、音乐和人物一起设置", en: "Set background, music, and character together" },
  dialogue: { zh: "对白", en: "Dialogue" },
  dialogueHint: { zh: "角色表情加一句台词", en: "A character look plus one line" },
  narration: { zh: "旁白", en: "Narration" },
  narrationHint: { zh: "旁白或主角内心", en: "Narration or inner voice" },
  choice: { zh: "选择", en: "Choice" },
  choiceHint: { zh: "让玩家在多个选项中决定", en: "Let the player decide between options" },
  moment: { zh: "关键画面", en: "Key moment" },
  momentHint: { zh: "CG 或全屏情绪画面", en: "CG or full-screen emotional beat" },
  more: { zh: "其他功能", en: "More" },
  moreHint: { zh: "人物、音乐、互动、状态与路线", en: "Characters, audio, interactions, state, routes" },
  compilerError: { zh: "这一帧包含编译错误", en: "This frame contains a compiler error" },
  concurrent: { zh: "与上一帧同时发生", en: "Happens with previous frame" },
  companions: { zh: "+{count} 同时", en: "+{count} together" },
  tools: { zh: "逻辑与状态 {count}", en: "Logic & state {count}" },
  toolsHint: { zh: "这些步骤不占用画面帧，但会影响路线、状态和好感。", en: "These do not consume a visual frame, but control routes, state, and affection." },
  beforeFrames: { zh: "开场前", en: "Before frame 1" },
  afterFrame: { zh: "第 {frame} 帧后", en: "After frame {frame}" },
  episodeStructure: { zh: "整集结构 · {count} 个块", en: "Episode structure · {count} sections" },
  chooseSection: { zh: "选择剧情块", en: "Choose story section" },
  sectionFrames: { zh: "{label} {count} 帧", en: "{label} · {count} frames" },
  jumpSection: { zh: "跳到第 {index} 块：{label}", en: "Jump to section {index}: {label}" },
  addSection: { zh: "添加剧情块", en: "Add story section" },
  addSectionShort: { zh: "新块", en: "Section" },
  addSectionHint: { zh: "先选择玩家要经历什么，再编辑块里的每一帧。", en: "Choose the experience, then edit its frames." },
  sectionScene: { zh: "新场景", en: "New scene" },
  sectionDialogue: { zh: "对白段", en: "Dialogue sequence" },
  sectionChoice: { zh: "选择段", en: "Choice section" },
  sectionMoment: { zh: "关键画面", en: "Key moment" },
  sectionInteraction: { zh: "互动段", en: "Interaction section" },
  addFrame: { zh: "在当前块内添加一帧", en: "Add a frame inside this section" },
  branchSummary: { zh: "{options} 个选项 · {exits} 个出口", en: "{options} options · {exits} exits" },
} satisfies Record<string, Entry>;

const ADVANCED: readonly { kind: InsertKind; label: Entry; icon: ReactElement }[] = [
  { kind: "char", label: { zh: "人物 / 表情", en: "Character / look" }, icon: <UserRound size={16} /> },
  { kind: "music", label: { zh: "背景音乐", en: "Background music" }, icon: <Music2 size={16} /> },
  { kind: "sfx", label: { zh: "音效", en: "Sound effect" }, icon: <Music2 size={16} /> },
  { kind: "phone", label: { zh: "手机对话", en: "Phone conversation" }, icon: <MessageCircle size={16} /> },
  { kind: "trick", label: { zh: "点击 / 手势互动", en: "Tap / gesture" }, icon: <Sparkles size={16} /> },
  { kind: "minigame", label: { zh: "小游戏", en: "Minigame" }, icon: <Sparkles size={16} /> },
  { kind: "pause", label: { zh: "点击继续", en: "Tap to continue" }, icon: <MoreHorizontal size={16} /> },
  { kind: "affection", label: { zh: "好感变化", en: "Affection" }, icon: <Sparkles size={16} /> },
  { kind: "signal", label: { zh: "剧情状态", en: "Story state" }, icon: <Sparkles size={16} /> },
  { kind: "gate", label: { zh: "路线出口", en: "Route exit" }, icon: <GitFork size={16} /> },
];

export interface StepFilmstripProps {
  readonly steps: readonly PresentationStep[];
  readonly sections?: readonly StoryboardSection[];
  readonly branches?: readonly StoryboardBranch[];
  readonly tools?: readonly PresentationStep[];
  readonly selectedLine: number | null;
  readonly activeSourceLine?: number | null;
  readonly onSelect: (line: number) => void;
  readonly onAdd: (kind: InsertKind, afterLine?: number | null) => void;
  readonly onMove?: (fromLine: number, toLine: number) => void;
  readonly onDelete?: (line: number) => void;
  readonly errorLines?: ReadonlySet<number>;
  readonly disabled?: boolean;
}

interface InsertPointProps {
  readonly afterLine: number | null;
  readonly disabled?: boolean;
  readonly onAdd: StepFilmstripProps["onAdd"];
}

function SectionInsertPoint({ afterLine, disabled, onAdd }: InsertPointProps): ReactElement {
  const tr = useTr();
  const [open, setOpen] = useState(false);
  const choose = (kind: InsertKind) => {
    onAdd(kind, afterLine);
    setOpen(false);
  };
  const templates: ReadonlyArray<{ kind: InsertKind; label: Entry; hint: Entry; icon: ReactElement }> = [
    { kind: "bg", label: S.sectionScene, hint: S.sceneHint, icon: <Image size={17} /> },
    { kind: "dialogue", label: S.sectionDialogue, hint: S.dialogueHint, icon: <MessageCircle size={17} /> },
    { kind: "choice", label: S.sectionChoice, hint: S.choiceHint, icon: <GitFork size={17} /> },
    { kind: "cg", label: S.sectionMoment, hint: S.momentHint, icon: <Sparkles size={17} /> },
    { kind: "trick", label: S.sectionInteraction, hint: { zh: "点击、手势或轻互动", en: "Tap, gesture, or lightweight interaction" }, icon: <Sparkles size={17} /> },
  ];
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      align="center"
      side="top"
      className="studio-section-template-popover"
      trigger={
        <button type="button" className="studio-section-insert" disabled={disabled} aria-label={tr(S.addSection)} title={tr(S.addSection)}>
          <Plus size={13} aria-hidden="true" /><span>{tr(S.addSectionShort)}</span>
        </button>
      }
    >
      <div className="studio-section-template">
        <header><strong>{tr(S.addSection)}</strong><span>{tr(S.addSectionHint)}</span></header>
        <div>{templates.map((template) => (
          <button key={template.kind} type="button" onClick={() => choose(template.kind)}>
            <span aria-hidden="true">{template.icon}</span>
            <span><strong>{tr(template.label)}</strong><small>{tr(template.hint)}</small></span>
          </button>
        ))}</div>
      </div>
    </Popover>
  );
}

function InsertPoint({ afterLine, disabled, onAdd }: InsertPointProps): ReactElement {
  const tr = useTr();
  const [open, setOpen] = useState(false);
  const [more, setMore] = useState(false);
  const choose = (kind: InsertKind) => {
    onAdd(kind, afterLine);
    setOpen(false);
    setMore(false);
  };
  const card = (kind: InsertKind, label: Entry, hint: Entry, icon: ReactElement) => (
    <button key={kind} type="button" className="studio-template" onClick={() => choose(kind)}>
      <span className="studio-template__icon" aria-hidden="true">{icon}</span>
      <span><strong>{tr(label)}</strong><small>{tr(hint)}</small></span>
    </button>
  );
  return (
    <Popover
      open={open}
      onOpenChange={(next) => { setOpen(next); if (!next) setMore(false); }}
      align="center"
      side="top"
      className="studio-template-popover"
      trigger={
        <button type="button" className="studio-insert-point" disabled={disabled} aria-label={tr(S.insertHere)} title={tr(S.addFrame)}>
          <Plus size={14} aria-hidden="true" />
        </button>
      }
    >
      <div className="studio-template-gallery">
        <div className="studio-template-gallery__head">
          {more ? <button type="button" className="studio-template-gallery__back" onClick={() => setMore(false)}><ChevronLeft size={15} />{tr(S.addTitle)}</button> : null}
          <strong>{more ? tr(S.more) : tr(S.addTitle)}</strong>
          <span>{tr(S.addHint)}</span>
        </div>
        {more ? (
          <div className="studio-template-gallery__advanced">
            {ADVANCED.map((item) => <button key={item.kind} type="button" onClick={() => choose(item.kind)}>{item.icon}<span>{tr(item.label)}</span></button>)}
          </div>
        ) : (
          <div className="studio-template-gallery__grid">
            {card("bg", S.scene, S.sceneHint, <Image size={18} />)}
            {card("dialogue", S.dialogue, S.dialogueHint, <MessageCircle size={18} />)}
            {card("narration", S.narration, S.narrationHint, <AlignLeft size={18} />)}
            {card("choice", S.choice, S.choiceHint, <GitFork size={18} />)}
            {card("cg", S.moment, S.momentHint, <Sparkles size={18} />)}
            <button type="button" className="studio-template" onClick={() => setMore(true)}>
              <span className="studio-template__icon" aria-hidden="true"><MoreHorizontal size={18} /></span>
              <span><strong>{tr(S.more)}</strong><small>{tr(S.moreHint)}</small></span>
              <ChevronRight size={16} aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
    </Popover>
  );
}

function SortableFrame({ step, index, selected, hasError, disabled, onSelect, onDelete }: {
  readonly step: PresentationStep;
  readonly index: number;
  readonly selected: boolean;
  readonly hasError: boolean;
  readonly disabled?: boolean;
  readonly onSelect: (line: number) => void;
  readonly onDelete?: (line: number) => void;
}): ReactElement {
  const tr = useTr();
  const draggable = !disabled && step.kind !== "episode" && step.kind !== "gate" && step.kind !== "option";
  const sortable = useSortable({ id: step.id, disabled: !draggable });
  const style: CSSProperties = { transform: CSS.Transform.toString(sortable.transform), transition: sortable.transition };
  return (
    <li ref={sortable.setNodeRef} style={style} data-dragging={sortable.isDragging || undefined}>
      <div className="studio-step-wrap">
        <button
          type="button"
          className="studio-step-thumb"
          aria-current={selected ? "step" : undefined}
          aria-invalid={hasError || undefined}
          data-selected={selected || undefined}
          data-error={hasError || undefined}
          onClick={() => onSelect(step.line)}
        >
          <span className="studio-step-thumb__number">{String(index + 1).padStart(2, "0")}</span>
          <span className="studio-step-thumb__label">{tr(step.label)}</span>
          <span className="studio-step-thumb__summary">{step.summary}</span>
          {step.concurrent ? <span className="studio-step-thumb__together" title={tr(S.concurrent)}>&amp;</span> : null}
          {step.companionCount ? <span className="studio-step-thumb__companions">{tr(S.companions, { count: step.companionCount })}</span> : null}
          {hasError ? <span className="studio-step-thumb__error" role="img" aria-label={tr(S.compilerError)}><TriangleAlert size={14} /></span> : null}
        </button>
        {draggable ? (
          <button type="button" className="studio-step-handle" aria-label={tr(S.drag)} title={tr(S.drag)} {...sortable.attributes} {...sortable.listeners}>
            <GripVertical size={14} aria-hidden="true" />
          </button>
        ) : null}
        {selected && onDelete && step.kind !== "episode" ? (
          <button type="button" className="studio-step-delete" aria-label={tr(S.remove)} title={tr(S.remove)} onClick={() => onDelete(step.line)}>
            <Trash2 size={13} aria-hidden="true" />
          </button>
        ) : null}
      </div>
    </li>
  );
}

export function StepFilmstrip({ steps, sections, branches = [], tools = [], selectedLine, activeSourceLine, onSelect, onAdd, onMove, onDelete, errorLines, disabled }: StepFilmstripProps): ReactElement {
  const tr = useTr();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const selectedIndex = Math.max(0, steps.findIndex((step) => step.line === selectedLine));
  const ids = useMemo(() => steps.map((step) => step.id), [steps]);
  const resolvedSections = useMemo<readonly StoryboardSection[]>(() => {
    if (sections && sections.length > 0) return sections;
    if (steps.length === 0) return [];
    return [{
      id: `${steps[0]!.id}:section`,
      kind: "scene",
      label: { zh: "本集", en: "Episode" },
      summary: steps[0]!.summary,
      startLine: steps[0]!.line,
      endLine: steps.at(-1)!.line,
      frameCount: steps.length,
      frames: steps,
    }];
  }, [sections, steps]);
  const activeSectionId = resolvedSections.find((section) => section.frames.some((frame) => frame.line === selectedLine))?.id;
  const activeSectionRef = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    const reduceMotion = globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    activeSectionRef.current?.scrollIntoView?.({ behavior: reduceMotion ? "auto" : "smooth", block: "nearest", inline: "center" });
  }, [activeSectionId]);
  const handleDragEnd = (event: DragEndEvent) => {
    if (!event.over || event.active.id === event.over.id || !onMove) return;
    const from = steps.find((step) => step.id === event.active.id);
    const to = steps.find((step) => step.id === event.over?.id);
    if (from && to) onMove(from.line, to.line);
  };
  const current = steps.length === 0 ? 0 : selectedIndex + 1;

  return (
    <nav className="studio-filmstrip" aria-label={tr(S.timeline)}>
      <div className="studio-filmstrip__header">
        <div className="studio-filmstrip__position">
          <strong>{tr(S.timeline)}</strong>
          <span>{tr(S.position, { current, total: steps.length })}</span>
        </div>
        {resolvedSections.length > 0 ? (
          <div className="studio-episode-map">
            <strong>{tr(S.episodeStructure, { count: resolvedSections.length })}</strong>
            <div className="studio-episode-map__segments" role="group" aria-label={tr(S.episodeStructure, { count: resolvedSections.length })}>
              {resolvedSections.map((section, index) => (
                <button
                  key={section.id}
                  type="button"
                  style={{ flex: `${Math.max(1, section.frameCount)} 1 0` }}
                  data-active={section.id === activeSectionId || undefined}
                  aria-pressed={section.id === activeSectionId}
                  aria-label={tr(S.jumpSection, { index: index + 1, label: tr(section.label) })}
                  onClick={() => onSelect(section.startLine)}
                >
                  <span>{index + 1}</span><strong>{tr(section.label)}</strong><small>{section.frameCount}</small>
                </button>
              ))}
            </div>
            <div className="studio-episode-map__mobile">
              <select
                aria-label={tr(S.chooseSection)}
                value={resolvedSections.find((section) => section.id === activeSectionId)?.startLine ?? resolvedSections[0]!.startLine}
                onChange={(event) => onSelect(Number(event.currentTarget.value))}
              >
                {resolvedSections.map((section, index) => (
                  <option key={section.id} value={section.startLine}>
                    {index + 1}. {tr(S.sectionFrames, { label: tr(section.label), count: section.frameCount })}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : <span />}
        <div className="studio-filmstrip__nav">
          {tools.length ? (
            <Popover
              align="end"
              side="top"
              className="studio-tools-popover"
              trigger={<button type="button" className="studio-tools-trigger" title={tr(S.toolsHint)} data-active={tools.some((step) => step.line === activeSourceLine) || undefined}><GitFork size={14} />{tr(S.tools, { count: tools.length })}</button>}
            >
              <div className="studio-tools-list">
                <header><strong>{tr(S.tools, { count: tools.length })}</strong><span>{tr(S.toolsHint)}</span></header>
                {tools.map((step) => (
                  <div key={step.id} className="studio-tools-list__row" data-selected={step.line === activeSourceLine || undefined}>
                    <button type="button" onClick={() => onSelect(step.line)}><span>{tr(step.label)}</span><small>{tr(steps.filter((frame) => frame.line < step.line).length ? S.afterFrame : S.beforeFrames, { frame: steps.filter((frame) => frame.line < step.line).length })} · {step.summary}</small></button>
                    {!disabled && onDelete ? <IconButton label={tr(S.remove)} size="sm" onClick={() => onDelete(step.line)}><Trash2 size={14} /></IconButton> : null}
                  </div>
                ))}
              </div>
            </Popover>
          ) : null}
          <IconButton label={tr(S.previous)} size="sm" disabled={steps.length === 0 || selectedIndex <= 0} onClick={() => steps[selectedIndex - 1] && onSelect(steps[selectedIndex - 1]!.line)}><ChevronLeft size={17} /></IconButton>
          <IconButton label={tr(S.next)} size="sm" disabled={steps.length === 0 || selectedIndex >= steps.length - 1} onClick={() => steps[selectedIndex + 1] && onSelect(steps[selectedIndex + 1]!.line)}><ChevronRight size={17} /></IconButton>
        </div>
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={ids} strategy={horizontalListSortingStrategy}>
          <ol className="studio-filmstrip__list">
            {resolvedSections.length === 0 ? <li className="studio-section-boundary"><SectionInsertPoint afterLine={null} disabled={disabled} onAdd={onAdd} /></li> : null}
            {resolvedSections.map((section, sectionIndex) => {
              const choiceLine = section.frames.find((frame) => frame.kind === "choice")?.line;
              const branch = choiceLine == null ? undefined : branches.find((candidate) => candidate.choiceLine === choiceLine);
              const branchSummary = branch ? tr(S.branchSummary, { options: branch.options.length, exits: branch.exits.length }) : section.summary;
              const active = section.id === activeSectionId;
              return <Fragment key={section.id}>
                <li className="studio-section-boundary">
                  <SectionInsertPoint afterLine={resolvedSections[sectionIndex - 1]?.endLine ?? null} disabled={disabled} onAdd={onAdd} />
                </li>
                <li ref={active ? activeSectionRef : undefined} className="studio-timeline-section" data-active={active || undefined} data-branch={branch ? true : undefined}>
                  <button type="button" className="studio-timeline-section__head" onClick={() => onSelect(section.startLine)}>
                    <span>{String(sectionIndex + 1).padStart(2, "0")}</span>
                    <strong>{tr(S.sectionFrames, { label: tr(section.label), count: section.frameCount })}</strong>
                    <small>{branchSummary}</small>
                  </button>
                  <ol className="studio-timeline-section__frames">
                    {section.frames.map((step) => {
                      const index = steps.findIndex((candidate) => candidate.id === step.id);
                      return (
                        <Fragment key={step.id}>
                          <SortableFrame step={step} index={index} selected={step.line === selectedLine} hasError={errorLines?.has(step.line) ?? false} disabled={disabled} onSelect={onSelect} onDelete={disabled ? undefined : onDelete} />
                          <li className="studio-filmstrip__insert"><InsertPoint afterLine={step.line} disabled={disabled} onAdd={onAdd} /></li>
                        </Fragment>
                      );
                    })}
                  </ol>
                </li>
              </Fragment>
            })}
            {resolvedSections.length > 0 ? <li className="studio-section-boundary"><SectionInsertPoint afterLine={resolvedSections.at(-1)?.endLine ?? null} disabled={disabled} onAdd={onAdd} /></li> : null}
          </ol>
        </SortableContext>
      </DndContext>
    </nav>
  );
}
